/**
 * Activity Feed Widget
 *
 * Shows real-time activity stream from the warehouse.
 * Auto-scrolls to show latest activities.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { useRef } from 'react';
import {
  PackageSearch,
  Truck,
  ArrowDownToLine,
  ArrowLeftRight,
  Calculator,
} from 'lucide-react';
import { WidgetInstance, ActivityEvent } from '../../types';
import { getRecentActivities } from '../../mockDataService';
import { fetchActivityFeed } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface ActivityFeedWidgetProps {
  instance: WidgetInstance;
}

const ActivityFeedWidget: React.FC<ActivityFeedWidgetProps> = ({ instance }) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<ActivityEvent[]>({
    widgetId: 'activity-feed',
    instance,
    fetchFn: async () => {
      try {
        return await fetchActivityFeed();
      } catch {
        return getRecentActivities();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="list" count={5} />
      </WidgetWrapper>
    );
  }

  const activities = data ?? [];

  const getActivityIcon = (type: ActivityEvent['type']) => {
    switch (type) {
      case 'pick':
        return <PackageSearch className="w-4 h-4 text-green-400" />;
      case 'receive':
        return <Truck className="w-4 h-4 text-blue-400" />;
      case 'putaway':
        return <ArrowDownToLine className="w-4 h-4 text-purple-400" />;
      case 'transfer':
        return <ArrowLeftRight className="w-4 h-4 text-yellow-400" />;
      case 'adjustment':
        return <Calculator className="w-4 h-4 text-orange-400" />;
    }
  };

  const getActivityColor = (type: ActivityEvent['type']) => {
    switch (type) {
      case 'pick':
        return 'bg-green-500/10 border-green-500/30';
      case 'receive':
        return 'bg-blue-500/10 border-blue-500/30';
      case 'putaway':
        return 'bg-purple-500/10 border-purple-500/30';
      case 'transfer':
        return 'bg-yellow-500/10 border-yellow-500/30';
      case 'adjustment':
        return 'bg-orange-500/10 border-orange-500/30';
    }
  };

  const formatTime = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);

    if (diff < 60) return t('manager.modules.wmsDashboard.activityFeed.secondsAgo', { '0': diff }, `${diff}s siden`);
    if (diff < 3600) return t('manager.modules.wmsDashboard.activityFeed.minutesAgo', { '0': Math.floor(diff / 60) }, `${Math.floor(diff / 60)}m siden`);
    return d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      <div ref={listRef} className="h-full overflow-y-auto space-y-2 pr-1">
        {activities.map((activity, index) => (
          <div
            key={activity.id}
            className={`
              flex items-start gap-2 p-2 rounded-lg border
              ${getActivityColor(activity.type)}
              ${index === 0 ? 'animate-pulse' : ''}
            `}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-white truncate">
                  {activity.description}
                </span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {formatTime(activity.timestamp)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                <span>{activity.operator}</span>
                <span className="text-gray-600">&bull;</span>
                <span className="font-mono">{activity.location}</span>
                <span className="text-gray-600">&bull;</span>
                <span>{t('manager.modules.wmsDashboard.activityFeed.qtyUnit', { '0': activity.qty }, `${activity.qty} stk`)}</span>
              </div>
            </div>
          </div>
        ))}

        {activities.length === 0 && !isLoading && (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            {t('manager.modules.wmsDashboard.activityFeed.empty', {}, 'Ingen aktivitet enda')}
          </div>
        )}
      </div>
    </WidgetWrapper>
  );
};

export default ActivityFeedWidget;
